import { useLazyQuery } from '@apollo/client';
import { Empty, Select, Spin, Tag } from 'antd';
import { debounce, uniqBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import {
  LIBRARY_FACILITY_TYPE,
  LIMIT,
  SORT,
} from '../../../../../common/constants';
import SelectComponent from '../../../../../components/SelectComponent';
import { LIBRARIES } from '../graphql/Queries';

const { Option } = Select;

const LOADING_OPTION_KEY = '__loading__';

const LibrarySelect = (props) => {
  const {
    library,
    libraryData,
    isModalOpen = true,
    isActiveLibOnly = false,
    showInactiveTag = false,
    selectedState,
    selectedCity,
    types = [
      LIBRARY_FACILITY_TYPE?.LIBRARY?.value,
      LIBRARY_FACILITY_TYPE?.WAREHOUSE?.value,
    ],
    ...rest
  } = props;
  const [libraryList, setLibraryList] = useState([]);
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [isAllLibrariesFetched, setIsAllLibrariesFetched] = useState(false);
  const [isLibrariesAllowClear, setIsLibrariesAllowClear] = useState(false);
  const [isLibrariesDropdownVisible, setIsLibrariesDropdownVisible] = useState(
    false,
  );
  const [
    isFetchMoreLibrariesLoading,
    setIsFetchMoreLibrariesLoading,
  ] = useState(false);
  const listRef = useRef(null);
  const isFetchingMoreRef = useRef(false);

  const isFiltering = !!(selectedCity || selectedState);
  const fetchLimit = isFiltering ? 1000 : LIMIT;

  const [libraries, { loading: isLibraryLoading }] = useLazyQuery(LIBRARIES, {
    onCompleted: (response) => {
      if (response?.libraries?.data?.length < fetchLimit) {
        setIsAllLibrariesFetched(true);
      }
      isFetchingMoreRef.current = false;
      setIsFetchMoreLibrariesLoading(false);
      if (libraryData) {
        setLibraryList(
          uniqBy(
            [
              {
                name: libraryData?.name,
                id: libraryData?.id,
                libraryNumber: libraryData?.libraryNumber,
                isActive: libraryData?.isActive,
              },
              ...libraryList,
              ...response?.libraries?.data,
            ],
            'id',
          ),
        );
        return;
      }
      setLibraryList([...libraryList, ...response?.libraries?.data]);
    },
    fetchPolicy: 'network-only',
    onError() {
      isFetchingMoreRef.current = false;
      setIsFetchMoreLibrariesLoading(false);
    },
  });

  useEffect(() => {
    setLibrarySearchTerm('');
    setLibraryList([]);
    if (isModalOpen) {
      libraries({
        variables: {
          filter: {
            limit: fetchLimit,
            skip: 0,
            search: librarySearchTerm,
            types,
            ...(isActiveLibOnly && { isActive: true }),
          },
          sort: {
            field: 'createdAt',
            order: SORT?.DESC,
          },
        },
      });
    }
    setIsAllLibrariesFetched(false);
  }, [isLibrariesDropdownVisible, isModalOpen, selectedState, selectedCity]);

  const handleLibrariesScroll = (event) => {
    if (isFiltering) return;
    if (
      !isAllLibrariesFetched &&
      !isLibraryLoading &&
      !isFetchingMoreRef.current
    ) {
      const target = event?.target;
      const currentLength = libraryList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 20
      ) {
        isFetchingMoreRef.current = true;
        setIsFetchMoreLibrariesLoading(true);
        libraries({
          variables: {
            filter: {
              limit: fetchLimit,
              skip: currentLength,
              search: librarySearchTerm,
              types,
              ...(isActiveLibOnly && { isActive: true }),
            },
            sort: {
              field: 'createdAt',
              order: SORT?.DESC,
            },
          },
        });
      }
    }
  };

  const handleLibrariesSearch = (searchTerm) => {
    const trimSearch = searchTerm?.trim();
    setIsAllLibrariesFetched(false);
    setLibraryList([]);
    setLibrarySearchTerm(trimSearch);
    libraries({
      variables: {
        filter: {
          limit: fetchLimit,
          skip: 0,
          search: trimSearch,
          types,
          ...(isActiveLibOnly && { isActive: true }),
        },
        sort: {
          field: 'createdAt',
          order: SORT?.DESC,
        },
      },
    });
  };

  const handleSearchLibrariesDebounce = debounce(handleLibrariesSearch, 500);

  useEffect(() => {
    handleSearchLibrariesDebounce?.cancel();
  }, [handleSearchLibrariesDebounce]);

  useEffect(() => {
    if (isLibraryLoading) {
      setIsLibrariesAllowClear(false);
    } else {
      setIsLibrariesAllowClear(true);
    }
  }, [isLibraryLoading]);

  useEffect(() => {
    if (!isFetchMoreLibrariesLoading || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [isFetchMoreLibrariesLoading]);

  return (
    <SelectComponent
      showSearch
      loading={isLibraryLoading && isLibrariesDropdownVisible}
      placeholder="Select Library"
      optionFilterProp="children"
      allowClear={isLibrariesAllowClear}
      onPopupScroll={(event) => {
        listRef.current = event?.target;
        handleLibrariesScroll(event);
      }}
      onSearch={handleSearchLibrariesDebounce}
      onDropdownVisibleChange={(visible) =>
        setIsLibrariesDropdownVisible(visible)
      }
      filterOption={false}
      notFoundContent={
        !isFetchMoreLibrariesLoading && isLibraryLoading ? (
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        ) : (
          <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
        )
      }
      {...rest}
    >
      {libraryList
        ?.filter((lib) => {
          if (lib?.id === library?.id) return false;
          if (selectedState && lib?.state !== selectedState) return false;
          if (selectedCity && lib?.city !== selectedCity) return false;
          return true;
        })
        ?.map((lib) => (
          <Option key={lib?.id} value={lib?.id} label={lib?.name}>
            {lib?.name} {lib?.libraryNumber ? `(${lib.libraryNumber})` : ''}
            {lib?.isActive === false && showInactiveTag && (
              <Tag color="red" className="ml-8">
                Inactive
              </Tag>
            )}
          </Option>
        ))}
      {isFetchMoreLibrariesLoading && (
        <Option key={LOADING_OPTION_KEY} value={LOADING_OPTION_KEY} disabled>
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        </Option>
      )}
    </SelectComponent>
  );
};

export default LibrarySelect;
